import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Stack from '@mui/material/Stack/Stack';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import setObjectFieldByPath from '@/shared/utils/setObjectFieldByPath';

import TileType from '../../components/Tile/types/tile.type';

type TileConfigDialogProps = {
  tile: TileType;
  onClose: () => void;
  onAddTile: (tile: TileType) => void;
};

const TileConfigDialog: FC<TileConfigDialogProps> = ({
  tile,
  onClose,
  onAddTile,
}) => {
  const { t } = useTranslation(['checkout', 'forms', 'common']);

  const [form, setForm] = useState({
    title: tile.title,
    description: tile.description,
    styles: {
      backgroundColor: tile.styles?.backgroundColor || '',
      color: tile.styles?.color || '',
    },
  });

  const [formErrors, setFormErrors] = useState({
    title: '',
    description: '',
  });

  const onValidateForm = () => {
    const errors = {
      title: '',
      description: '',
    };

    if (!form.title) {
      errors.title = t('forms:tile_title_required');
    }

    if (!form.description) {
      errors.description = t('forms:tile_description_required');
    }

    setFormErrors(errors);
    return Object.values(errors).some((msg) => msg !== '');
  };

  return (
    <ModalNotification
      size='xs'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: t('checkout:modifyTile'),
        content: (
          <Stack sx={{ py: 1, gap: 2 }}>
            <WaveUIForm_V2
              fields={[
                {
                  field_type: FieldType.Text,
                  field_props: {
                    name: 'title',
                    label: t('forms:tile_title'),
                    placeholder: t('forms:tile_title_placeholder'),
                    value: form.title,
                    required: true,
                    error: !!formErrors.title,
                    helperText: formErrors.title,
                  },
                },
                {
                  field_type: FieldType.Text,
                  field_props: {
                    name: 'description',
                    label: t('forms:tile_description'),
                    placeholder: t('forms:tile_description_placeholder'),
                    value: form.description,
                    required: true,
                    error: !!formErrors.description,
                    helperText: formErrors.description,
                  },
                },
                {
                  columns: 6,
                  field_type: FieldType.ColorPicker,
                  field_props: {
                    name: 'styles.backgroundColor',
                    label: t('forms:tile_background_color'),
                    placeholder: t('forms:tile_background_color_placeholder'),
                    value: form.styles.backgroundColor,
                    required: true,
                  },
                },
                {
                  columns: 6,
                  field_type: FieldType.ColorPicker,
                  field_props: {
                    name: 'styles.color',
                    label: t('forms:tile_text_color'),
                    placeholder: t('forms:tile_text_color_placeholder'),
                    value: form.styles.color,
                    required: true,
                  },
                },
              ]}
              getFieldsValues={(fieldValue) => {
                const [key, value] = Object.entries(fieldValue)[0];

                setForm((prev) => ({
                  ...prev,
                  ...setObjectFieldByPath({ path: key, value, source: prev }),
                }));
              }}
            />
          </Stack>
        ),
        actions: [
          {
            variant: 'text',
            label: t('common:cancel'),
            onClick: onClose,
          },
          {
            variant: 'contained',
            fullWidth: true,
            label: t('checkout:addTile'),
            onClick: () => {
              const isFormValid = onValidateForm();

              if (isFormValid) return;

              onAddTile({ ...tile, ...form });
            },
          },
        ],
      }}
    />
  );
};

export default TileConfigDialog;
